import { computed, ref } from 'vue'

import { readAnalyticsConsent } from '../utils/analytics-consent'
import { updateAnalyticsConsent } from '../utils/posthog-tracker'

const cookieConsent = ref<boolean | null>(readAnalyticsConsent())
let consentSyncInstalled = false

function syncConsentFromSharedCookie() {
  const latestConsent = readAnalyticsConsent()
  if (latestConsent === cookieConsent.value)
    return
  cookieConsent.value = latestConsent
  updateAnalyticsConsent(latestConsent)
}

function installConsentSync() {
  if (consentSyncInstalled || typeof window === 'undefined')
    return
  consentSyncInstalled = true
  window.addEventListener('focus', syncConsentFromSharedCookie)
  window.addEventListener('storage', syncConsentFromSharedCookie)
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden)
      syncConsentFromSharedCookie()
  })
}

installConsentSync()

/**
 * Composable for managing cookie consent
 *
 * Usage:
 * ```ts
 * const { hasConsented, canUseAnalytics, showConsentBanner } = useCookieConsent()
 *
 * if (canUseAnalytics.value) {
 *   // Initialize analytics only if user consented
 * }
 * ```
 */
export function useCookieConsent() {
  // Check if user has made a decision
  const hasConsented = computed(() => cookieConsent.value !== null)

  // Check if user accepted cookies
  const hasAccepted = computed(() => cookieConsent.value === true)

  // Check if user rejected cookies
  const hasRejected = computed(() => cookieConsent.value === false)

  // Show banner only if user hasn't made a decision
  const showConsentBanner = computed(() => !hasConsented.value)

  // Can use analytics/tracking cookies (only if accepted)
  const canUseAnalytics = computed(() => hasAccepted.value)

  // Can use functional cookies (always allowed, even if rejected)
  // Functional cookies are necessary for the site to work
  const canUseFunctional = computed(() => true)

  function acceptCookies() {
    cookieConsent.value = true
    updateAnalyticsConsent(true)
  }

  function rejectCookies() {
    cookieConsent.value = false
    updateAnalyticsConsent(false)
  }

  function resetConsent() {
    cookieConsent.value = null
    updateAnalyticsConsent(null)
  }

  return {
    cookieConsent,
    hasConsented,
    hasAccepted,
    hasRejected,
    showConsentBanner,
    canUseAnalytics,
    canUseFunctional,
    acceptCookies,
    rejectCookies,
    resetConsent,
  }
}
